import { BitkitEmptyState, IconTasks } from '@bitrise/bitkit-v2';
import { Grid, GridItem } from '@chakra-ui/react/grid';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceCallback, useEventListener, useResizeObserver } from 'usehooks-ts';

import useDidMountEffect from '@/hooks/useDidMountEffect';
import useEffectOnce from '@/hooks/useEffectOnce';
import useQueryParams from '@/hooks/useQueryParams';
import { mergeQueryParams } from '@/utils/queryParams';

import TestCaseDetails from '../TestCaseDetails/TestCaseDetails';
import TestSuiteDetails from '../TestCaseDetails/TestSuiteDetails';
import {
  trackTestCaseDetailsViewed,
  trackTestsFilterChanged,
  trackTestsTabDisplayed,
  trackTestSuiteDetailsViewed,
  updateTestResultsTrackingFields,
} from '../TestResults.tracking';
import { TestCase, TestFilter, TestFilters, TestReport, TestSuite } from '../TestResults.types';
import { testCaseIdentifierOf } from '../TestResults.utils';
import TestsHeader from '../TestsHeader/TestsHeader';
import TestQuarantineAlert from './TestQuarantineAlert';
import { calculateSummary, getConcatenatedStageAndWorkflowNameOf } from './TestResultsExplorer.utils';
import TestResultsTree from './TestResultsTree';
import useFilteredReport from './useFilteredReport';

const TestResultsTreeMemo = memo(TestResultsTree);
const TestCaseDetailsMemo = memo(TestCaseDetails);
const TestSuiteDetailsMemo = memo(TestSuiteDetails);

type SelectedItem = { path: string; item: TestCase | TestSuite; type: 'testCase' | 'testSuite' };

type Props = {
  report: TestReport;
  projectSlug: string;
  runnableType: 'build' | 'pipeline';
  runnableSlug: string;
  runnableNumber: number;
};

const TestResultsExplorer = ({
  report: initialReport,
  projectSlug,
  runnableType,
  runnableSlug,
  runnableNumber,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  const { open, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState<SelectedItem>();
  const [containerHeight, setContainerHeight] = useState(0);

  const queryParams = useQueryParams();
  const initialSummary = useMemo(() => calculateSummary(initialReport), [initialReport]);

  const filters = useMemo<TestFilters>(() => {
    const status = queryParams.get('tests_filter_status') as TestFilter | null;

    return {
      status: status ?? (initialSummary.failed > 0 ? 'failed' : 'all'),
      search: queryParams.get('tests_filter_search') ?? '',
    };
  }, [queryParams, initialSummary.failed]);

  const { summary, report } = useFilteredReport(initialReport, filters);

  const selectItem = useCallback(
    (path: string, item: TestCase | TestSuite, type: 'testCase' | 'testSuite') => {
      if (type === 'testCase') {
        updateTestResultsTrackingFields({ testSuite: path, testCaseName: item.name });
        trackTestCaseDetailsViewed({ detailType: 'test_output' });
      }
      if (type === 'testSuite') {
        updateTestResultsTrackingFields({ testSuite: path });
        trackTestSuiteDetailsViewed({ detailType: 'attachments' });
      }

      setSelected({ path, item, type });
      onOpen();
    },
    [onOpen],
  );

  const closePanel = useCallback(() => {
    setSelected(undefined);
    onClose();
  }, [onClose]);

  const handleStatusChange = useCallback(
    (status: TestFilter) => {
      mergeQueryParams({ set: [{ type: 'tests_filter_status', value: status }], unset: [] });
      closePanel();
    },
    [closePanel],
  );

  const handleSearchChange = useCallback(
    (search: string) => {
      mergeQueryParams(
        search
          ? { set: [{ type: 'tests_filter_search', value: search }], unset: [] }
          : { set: [], unset: ['tests_filter_search'] },
      );
      closePanel();
    },
    [closePanel],
  );

  const updateContainerHeight = useDebounceCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const top = rect.top + window.scrollY;
    const footerHeight = document.querySelector('#footer')?.clientHeight || 0;

    setContainerHeight(window.innerHeight - top - footerHeight - 32);
  }, 100);

  useEventListener('resize', updateContainerHeight);
  useEffect(updateContainerHeight, [updateContainerHeight]);
  useResizeObserver({ ref: leftPanelRef, onResize: updateContainerHeight });
  useResizeObserver({ ref: rightPanelRef, onResize: updateContainerHeight });

  // Tracking
  const testStatusFilter = filters.status;
  useEffect(() => updateTestResultsTrackingFields({ testStatusFilter }), [testStatusFilter]);
  useEffectOnce(() => trackTestsTabDisplayed({ tabState: 'tree_view' }));
  useDidMountEffect(() => trackTestsFilterChanged(), [testStatusFilter]);

  const renderDetails = () => {
    if (!open || !selected) return null;

    const { path, item, type } = selected;

    if (type === 'testCase') {
      const testCase = item as TestCase;

      return (
        <TestCaseDetailsMemo
          runs={testCase.runs}
          name={testCase.name}
          status={testCase.status}
          isFlaky={testCase.flaky}
          breadcrumb={path}
          testCaseClassName={testCase.className}
          testCaseIdentifier={testCaseIdentifierOf(testCase)}
          durationInMs={testCase.durationInMs}
          message={testCase.message}
          attachments={testCase.attachments}
          workflowName={getConcatenatedStageAndWorkflowNameOf(testCase, report.testSuites)}
          onClose={closePanel}
          testSuites={testCase.testSuites}
          projectSlug={projectSlug}
          runnableType={runnableType}
          runnableSlug={runnableSlug}
          runnableNumber={runnableNumber}
        />
      );
    }

    return (
      <TestSuiteDetailsMemo
        name="Test run details"
        breadcrumb={path}
        attachments={(item as TestSuite).attachments ?? []}
        onClose={closePanel}
      />
    );
  };

  return (
    <>
      <TestsHeader
        filters={filters}
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        summary={summary}
      />
      {filters.status === 'skipped' && <TestQuarantineAlert projectSlug={projectSlug} />}
      <Grid
        marginBlockStart="16"
        gap="16"
        ref={containerRef}
        height={['auto', `max(60dvh, ${containerHeight}px)`]}
        templateColumns={['1fr', open ? '1fr 1fr' : '1fr']}
      >
        <GridItem ref={leftPanelRef} height="100%" overflowY="auto" display={[open ? 'none' : 'block', 'block']}>
          {report.testSuites.length > 0 ? (
            <TestResultsTreeMemo
              filters={filters}
              suites={report.testSuites}
              onTestCaseClick={(path, testCase) => selectItem(path, testCase, 'testCase')}
              onTestSuiteDetailsClick={(path, testSuite) => selectItem(path, testSuite, 'testSuite')}
            />
          ) : (
            <BitkitEmptyState
              icon={IconTasks}
              headingText="No tests to display"
              bodyText="Your current filter settings do not match any tests."
            />
          )}
        </GridItem>
        <GridItem ref={rightPanelRef} minWidth="0" height="100%" overflowY="auto">
          {renderDetails()}
        </GridItem>
      </Grid>
    </>
  );
};

export default TestResultsExplorer;
